/**
 * MentionTextarea — a controlled textarea with an `@`-trigger dropdown of
 * candidate users. Picking a candidate inserts `@displayName` and registers
 * the userId in the `mentions` set (so the parent can post `mentions[]` to
 * the backend).
 *
 * Behaviour:
 *   - The dropdown opens when the user types `@` followed by a query
 *     (letters, digits, dots, dashes, underscores). Closing happens on
 *     Escape / click outside / blur.
 *   - Arrow up / down navigates candidates; Enter / Tab selects the
 *     highlighted one. Plain Enter while a candidate is highlighted does
 *     NOT insert a newline (so the form doesn't submit accidentally).
 *   - Already-inserted mentions are rendered as a visible token
 *     `[name](id)` so the body has the userId embedded, but on submit the
 *     parent receives both the raw body and the unique mention set.
 *   - The dropdown also accepts a click on a row.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useProfiles, pickDisplay, type PublicProfile } from '@/features/profiles/profiles'
import { GFM_TABLE_SKELETON } from '@/lib/tiptap/markdownTable'
import { cn } from '@/lib/utils'
import { UserAvatar } from '@/components/user/UserAvatar'

export interface MentionCandidate {
  id: string
  /** Pre-resolved display for sorting + filter (alias, name or email local). */
  label: string
}

export interface MentionTextareaProps {
  value: string
  onChange: (next: string) => void
  onMentionsChange?: (mentionIds: string[]) => void
  candidates: MentionCandidate[]
  placeholder?: string
  className?: string
  rows?: number
  disabled?: boolean
  /** Stop Enter from submitting a wrapping form while the popup is open. */
  onSubmit?: () => Promise<void> | void
  /** Submit while the popup is closed. Returns when sending is done. */
  submitRef?: React.MutableRefObject<(() => Promise<void> | void) | null>
}

export function MentionTextarea({
  value,
  onChange,
  onMentionsChange,
  candidates,
  placeholder,
  className,
  rows = 3,
  disabled,
  onSubmit,
  submitRef,
}: MentionTextareaProps) {
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [anchorStart, setAnchorStart] = useState<number | null>(null) // index of '@' in the text
  const [highlight, setHighlight] = useState(0)

  // Pull profiles for all candidate ids so we can show real display names.
  const { profiles } = useProfiles(candidates.map((c) => c.id))

  // List of resolved candidates sorted by relevance to the current query.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return candidates
      .map((c) => {
        const profile: PublicProfile | undefined = profiles[c.id]
        const display = pickDisplay(c.id, profile, { displayName: c.label })
        return { id: c.id, label: display.displayName, secondary: display.email ?? null, avatarSources: display.avatarSources }
      })
      .filter((c) => (q ? c.label.toLowerCase().includes(q) || (c.secondary ?? '').toLowerCase().includes(q) : true))
      .sort((a, b) => a.label.localeCompare(b.label))
      .slice(0, 8)
  }, [candidates, profiles, query])

  useEffect(() => {
    setHighlight((h) => (filtered.length === 0 ? 0 : Math.min(h, filtered.length - 1)))
  }, [filtered.length])

  function getTrigger(text: string, caret: number): { start: number; query: string } | null {
    // Walk back from caret to find '@' that isn't part of an email/word.
    let i = caret - 1
    while (i >= 0) {
      const ch = text[i]
      if (ch === '@') {
        // '@' is only a trigger if it's the first char or preceded by whitespace / punctuation.
        const prev = i > 0 ? text[i - 1] : ' '
        if (/\s|[(\[{>,;:]/.test(prev)) {
          return { start: i, query: text.slice(i + 1, caret) }
        }
        return null
      }
      if (/\s/.test(ch)) return null
      i--
    }
    return null
  }

  function handleChange(next: string) {
    onChange(next)
    const ta = taRef.current
    if (!ta) return
    const caret = ta.selectionStart ?? next.length
    const trig = getTrigger(next, caret)
    if (trig) {
      setAnchorStart(trig.start)
      setQuery(trig.query)
      setOpen(true)
    } else {
      setOpen(false)
      setAnchorStart(null)
      setQuery('')
    }
    // Recompute mentions each time the body changes.
    publishMentions(next)
  }

  function publishMentions(text: string) {
    if (!onMentionsChange) return
    // Match `@name` tokens; the body just stores the name, not the id. We
    // recover the id by matching the name against resolved display names.
    const seen = new Set<string>()
    const out: string[] = []
    const re = /@([A-Za-z0-9._-]+)/g
    let m: RegExpExecArray | null
    while ((m = re.exec(text))) {
      const label = m[1]
      const found = candidates.find((c) => {
        const p = profiles[c.id]
        const d = pickDisplay(c.id, p, { displayName: c.label })
        return d.displayName.toLowerCase() === label.toLowerCase()
      })
      if (found && !seen.has(found.id)) {
        seen.add(found.id)
        out.push(found.id)
      }
    }
    onMentionsChange(out)
  }

  function applyCandidate(c: { id: string; label: string }) {
    if (anchorStart == null) return
    const before = value.slice(0, anchorStart)
    const caret = taRef.current?.selectionStart ?? value.length
    const after = value.slice(caret)
    const insert = `@${c.label} `
    const next = before + insert + after
    onChange(next)
    setOpen(false)
    setAnchorStart(null)
    setQuery('')
    // Move caret to end of inserted token.
    requestAnimationFrame(() => {
      const ta = taRef.current
      if (!ta) return
      const pos = before.length + insert.length
      ta.focus()
      ta.setSelectionRange(pos, pos)
    })
    publishMentions(next)
  }

  /**
   * Drops a GFM table skeleton at the caret (or replaces the selection).
   * The text around the caret is kept as it is — the table only adds its
   * own lines.
   */
  function insertTable() {
    const ta = taRef.current
    // Use the caret only when the textarea has the focus; otherwise the
    // table is appended at the end of the text.
    const focused = !!ta && document.activeElement === ta
    const start = focused ? ta!.selectionStart : value.length
    const end = focused ? ta!.selectionEnd : value.length
    const before = value.slice(0, start)
    const after = value.slice(end)
    // A table has to sit on its own lines to be valid GFM.
    const prefix = before.length > 0 && !before.endsWith('\n') ? '\n' : ''
    const suffix = after.length > 0 && !after.startsWith('\n') ? '\n' : ''
    const insertion = `${prefix}${GFM_TABLE_SKELETON}${suffix}`
    const next = before + insertion + after
    onChange(next)
    publishMentions(next)
    requestAnimationFrame(() => {
      const el = taRef.current
      if (!el) return
      const pos = before.length + insertion.length
      el.focus()
      el.setSelectionRange(pos, pos)
    })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (open && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlight((h) => (h + 1) % filtered.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlight((h) => (h - 1 + filtered.length) % filtered.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        applyCandidate(filtered[highlight])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
        return
      }
    }
    if (!open && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void onSubmit?.()
    }
  }

  // Expose a submit handle for the parent so the comment submit button works.
  useEffect(() => {
    if (submitRef) submitRef.current = () => onSubmit?.()
  }, [submitRef, onSubmit])

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={cn('input min-h-[3rem] resize-none text-sm', className)}
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute z-30 mt-1 w-64 rounded-md border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-800"
          role="listbox"
        >
          {filtered.map((c, i) => (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault()
                applyCandidate(c)
              }}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
                i === highlight ? 'bg-surface-100 dark:bg-surface-700' : 'hover:bg-surface-50 dark:hover:bg-surface-700/60',
              )}
            >
              <UserAvatar name={c.label} email={c.secondary} sources={c.avatarSources ?? []} className="h-5 w-5 text-[9px]" pixelSize={40} decorative />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{c.label}</span>
                {c.secondary && (
                  <span className="block truncate text-[10px] text-surface-500">{c.secondary}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="mt-1 flex justify-end">
        <button
          type="button"
          onClick={insertTable}
          disabled={disabled}
          aria-label="Insert table"
          title="Insert a table"
          className="btn-ghost text-xs"
        >
          Insert table
        </button>
      </div>
    </div>
  )
}
